import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { TabsBar } from '@/common/TabsBar';

export const Route = createFileRoute(
  '/_app/_users/members/$memberId/follow-ups',
)({
  component: MemberFollowUpsRoute,
});

export default function MemberFollowUpsRoute() {
  const { t } = useTranslation();
  const { memberId } = Route.useParams();
  const studentId = Number(memberId);

  // Legacy refuses this tab by not registering it, so a denied viewer gets a
  // 404 page, not a permission message. The probe answers 200 for every
  // signed-in caller, so it isn't an existence oracle; one query decides the
  // page and carries the caption the tables need.
  const { data: access, isLoading } = api.getStudentFollowUpsAccess.useQuery({
    path: { student_id: studentId },
  });

  // Not a no-op: rendering the tab strip before `access` resolves flashes it at
  // a viewer the next line refuses.
  if (isLoading) return null;

  // Positive test: `access` is undefined on a 401, a 500 or a dropped
  // connection, and each of those is a refusal.
  if (access?.can_view !== true) return <NotFoundScreen />;

  // Exactly three, in legacy's order.
  const tabs = [
    { title: t('wbs.follow-up-requests'), to: '' },
    { title: t('wbs.pending-requests'), to: 'pending-requests' },
    { title: t('sidebar.follow-ups'), to: 'entries' },
  ];

  return (
    <>
      <TabsBar
        tabs={tabs}
        base={'/members/$memberId/follow-ups'}
        tabLevel={4}
      />
      <MainContent>
        <Outlet />
      </MainContent>
    </>
  );
}

const MainContent = styled.main`
  background: var(--color-background);
  flex: 1;
  min-width: 0;
`;
